import type { BaseCondition, ConditionGroup, OfferDefinition, OfferSystemConfig } from '../offerSystemTypes';
import { getConditionHandler } from '../getConditionHandler';
import { MS_IN_HOUR } from '../../../utils/time-utils';

function createDivElement(innerText: string, style: Partial<CSSStyleDeclaration> = {}): HTMLDivElement {
  const div = document.createElement('div');
  div.innerText = innerText;
  Object.assign(div.style, style);
  return div;
}

interface DebugOfferViewConfig<S, C extends BaseCondition, GD> {
  offer: OfferDefinition<C, GD>;
  systemConfig: OfferSystemConfig<S, C, GD>;
}

interface EditorOfferViewContextData<C extends BaseCondition> {
  report: Map<ConditionGroup<C>, boolean[]>;
  remainingTime: number | null;
  activeVariant: number | null;
}

export class EditorOfferView<S, C extends BaseCondition, GD> {
  public readonly element = document.createElement('div');

  private readonly startConditions: HTMLDivElement[] = [];

  private readonly endConditions: HTMLDivElement[] = [];

  private readonly offer: OfferDefinition<C, GD>;

  private contextData: EditorOfferViewContextData<C> | null = null;

  constructor(private readonly config: DebugOfferViewConfig<S, C, GD>) {
    this.offer = config.offer;

    Object.assign(this.element.style, {
      backgroundColor: '#F5F5F5',
      fontFamily: 'SANS-SERIF',
      color: '#2C2C2C',
      borderRadius: '1em',
      padding: '0.7em',
      margin: '0.5em',
      border: '2px solid #2C2C2C',
    });

    this.refresh();
  }

  public setContextData(data: EditorOfferViewContextData<C> | null): void {
    this.contextData = data;
  }

  public refresh(): void {
    this.startConditions.length = 0;
    this.endConditions.length = 0;
    Array.from(this.element.children).forEach((child) => {
      this.element.removeChild(child);
    });
    this.createHTML();
  }

  private createHTML(): void {
    this.element.appendChild(createDivElement(this.offer.id, { fontSize: '1.2em', fontWeight: 'bold' }));
    this.element.appendChild(createDivElement(`Priority: ${this.offer.priority}`, { fontSize: '0.8em' }));

    this.createLocaLine(this.offer);

    this.createTimeLine();

    if (this.offer.location) {
      this.element.appendChild(createDivElement(`📍${this.offer.location.join(', ')}`));
    }

    if (this.offer.triggers.length > 0) {
      this.element.appendChild(createDivElement(this.offer.triggers.join(', ')));
    }

    if (this.offer.tags.length > 0) {
      this.element.appendChild(createDivElement(`🏷 ${this.offer.tags.join(', ')}`));
    }

    if (this.offer.startConditions) {
      this.element.appendChild(this.createMainConditionGroup('Start conditions:', this.offer.startConditions));
    }

    if (this.offer.endConditions) {
      this.element.appendChild(this.createMainConditionGroup('End conditions:', this.offer.endConditions));
    }

    this.createVariants();
  }

  private createVariants(): void {
    const root = createDivElement('Variants:');
    this.element.appendChild(root);

    const activeVariant = this.contextData?.activeVariant ?? null;

    const variantsToDisplay = activeVariant !== null ? [this.offer.variants[activeVariant]] : this.offer.variants;

    variantsToDisplay.forEach((variant) => {
      const variantRoot = createDivElement('');
      Object.assign(variantRoot.style, {
        border: '1px solid #2C2C2C',
        borderRadius: '1em',
        padding: '0.5em',
        margin: '0.5em',
      });
      root.appendChild(variantRoot);

      if (variant.conditions) {
        variantRoot.appendChild(this.createMainConditionGroup('Variant conditions', variant.conditions));
      }

      variantRoot.appendChild(
        createDivElement(
          `${variant.platformId} (${(variant.iapCostCents / 100).toFixed(2)}$) ${variant.badge ? `🚩${variant.badge.en_US}` : ''}`,
        ),
      );
      variantRoot.appendChild(this.createContent(variant.content));
    });
  }

  private createTimeLine(): void {
    const textParts = [];
    const { durationMinutes, endDate } = this.offer.timeLimit;

    if (durationMinutes !== undefined) {
      textParts.push(`${durationMinutes} minutes`);
    } else if (endDate) {
      textParts.push(`End date: ${new Date(endDate).toUTCString()}`);
    }

    const remainingTime = this.contextData?.remainingTime ?? null;

    if (remainingTime !== null) {
      textParts.push(`(${(remainingTime / MS_IN_HOUR).toFixed(2)}H remains)`);
    }

    if (textParts.length === 0) return;

    this.element.appendChild(createDivElement(`⏰️${textParts.join(' / ')}`));
  }

  private createLocaLine({ displayName }: OfferDefinition<C, GD>): void {
    if (!displayName) return;
    const text = displayName.en_US ?? '---';
    this.element.appendChild(createDivElement(text));
  }

  private createMainConditionGroup(label: string, conditionGroup: ConditionGroup<C>): HTMLDivElement {
    const div = createDivElement(label);
    const report = this.contextData?.report?.get?.(conditionGroup) ?? null;
    div.appendChild(this.createConditionsGroup(conditionGroup.groupType, conditionGroup.conditions, report));
    return div;
  }

  private createConditionsGroup(
    type: 'AND' | 'OR',
    conditions: (ConditionGroup<C> | C)[],
    debugReport: boolean[] | null,
  ): HTMLDivElement {
    const rootDiv = createDivElement('', { fontWeight: 'bold' });
    Object.assign(rootDiv.style, {
      border: type === 'AND' ? '1px solid #2C2C2C' : '1px dashed #2C2C2C',
      borderRadius: '1em',
      padding: '0.5em',
      margin: '0.5em',
    });

    conditions.forEach((condition: ConditionGroup<C> | C, index) => {
      if ('groupType' in condition) {
        const debugForGroup = this.contextData?.report?.get?.(condition) ?? null;
        rootDiv.appendChild(this.createConditionsGroup(condition.groupType, condition.conditions, debugForGroup));
      } else {
        rootDiv.appendChild(this.createConditionView(condition, debugReport?.[index] ?? null));
      }
    });

    return rootDiv;
  }

  private createConditionView(condition: C, passed: boolean | null): HTMLDivElement {
    const { systemConfig } = this.config;

    const handler = getConditionHandler(systemConfig, condition.type as Extract<C, 'type'>);

    let emoji = '❔';
    if (passed) {
      emoji = passed ? '✅' : '🚫';
    }
    return createDivElement(emoji + handler.toString(condition));
  }

  private createContent(content: Record<string, number>): HTMLDivElement {
    const contentString = Object.entries(content)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    return createDivElement(contentString);
  }

  public setVisibility(value: boolean): void {
    this.element.style.display = value ? 'block' : 'none';
  }
}
